/**
 * ModelHomeSection — one band on the model home page.
 *
 * Serif title + sans subtitle on the left, an optional action slot on the
 * right, content below. Bands are separated by hairlines only (no cards,
 * no shadows) per the editorial design system — and the last band on a page
 * drops its rule, since there is nothing below it to separate from. A band
 * has no side padding of its own: the PageContainer's inset is the page's,
 * so a band's rule and content line up with the header above it and with
 * every other page. `SectionHeading` is the bare header row for bands that
 * place two headed columns side by side.
 */

interface SectionHeadingProps {
  title: string
  subtitle?: string
  action?: React.ReactNode
}

export function SectionHeading({
  title,
  subtitle,
  action,
}: SectionHeadingProps): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
      <div>
        <h2 className="font-serif text-xl font-normal leading-tight">
          {title}
        </h2>
        {subtitle && (
          <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
        )}
      </div>
      {action}
    </div>
  )
}

interface ModelHomeSectionProps extends SectionHeadingProps {
  children: React.ReactNode
  className?: string
}

export function ModelHomeSection({
  title,
  subtitle,
  action,
  children,
  className,
}: ModelHomeSectionProps): React.JSX.Element {
  return (
    // Bands separate from each OTHER, so the last one on a page draws no
    // rule — a trailing hairline underlines empty space.
    <section
      className={`border-b border-border py-6 last:border-b-0 ${className ?? ''}`}
    >
      <SectionHeading title={title} subtitle={subtitle} action={action} />
      <div className="mt-5">{children}</div>
    </section>
  )
}
